/**
 * Space grid placement (ADR-0015). Mirrors backend/omni/routers/space.py:pack.
 * Tiles have explicit cells: x = column 0..3, y = row. Gaps are allowed.
 */
export const COLUMNS = 4;

export interface Placed {
  id: string;
  size: string;
  x?: number | null;
  y?: number | null;
}

export function dims(size: string): [number, number] {
  const [w, h] = size.split("x").map(Number);
  return [w, h];
}

class Occupancy {
  private cells = new Set<string>();
  fits(x: number, y: number, w: number, h: number): boolean {
    if (x < 0 || y < 0 || x + w > COLUMNS) return false;
    for (let cx = x; cx < x + w; cx++) for (let cy = y; cy < y + h; cy++) if (this.cells.has(`${cx},${cy}`)) return false;
    return true;
  }
  take(x: number, y: number, w: number, h: number) {
    for (let cx = x; cx < x + w; cx++) for (let cy = y; cy < y + h; cy++) this.cells.add(`${cx},${cy}`);
  }
  /** First free spot scanning row by row from `fromRow` (then from the top). */
  firstFree(w: number, h: number, fromRow = 0): [number, number] {
    for (let y = fromRow; ; y++) {
      for (let x = 0; x <= COLUMNS - w; x++) if (this.fits(x, y, w, h)) return [x, y];
      if (y > fromRow + 400) break;
    }
    return [0, fromRow + 400];
  }
}

/** Keep valid positions (first come, first served); place the rest in free cells. */
export function pack<T extends Placed>(items: T[]): (T & { x: number; y: number })[] {
  const occ = new Occupancy();
  const out: (T & { x: number; y: number })[] = new Array(items.length);
  const pending: number[] = [];
  items.forEach((it, i) => {
    const [w, h] = dims(it.size);
    if (it.x != null && it.y != null && occ.fits(it.x, it.y, w, h)) {
      occ.take(it.x, it.y, w, h);
      out[i] = { ...it, x: it.x, y: it.y };
    } else pending.push(i);
  });
  for (const i of pending) {
    const [w, h] = dims(items[i].size);
    const [x, y] = occ.firstFree(w, h);
    occ.take(x, y, w, h);
    out[i] = { ...items[i], x, y };
  }
  return out;
}

/**
 * Put `id` at (x, y) and move whatever it covers out of the way: tiles that
 * still fit keep their cells; displaced ones go to the nearest free cells at
 * or below their old row. Order in the array is preserved.
 */
export function moveTo<T extends Placed>(items: (T & { x: number; y: number })[], id: string, x: number, y: number): (T & { x: number; y: number })[] {
  const dragged = items.find((i) => i.id === id);
  if (!dragged) return items;
  const [dw, dh] = dims(dragged.size);
  const nx = Math.max(0, Math.min(COLUMNS - dw, x));
  const ny = Math.max(0, y);
  const occ = new Occupancy();
  occ.take(nx, ny, dw, dh);
  const next = new Map<string, { x: number; y: number }>([[id, { x: nx, y: ny }]]);
  const others = items.filter((i) => i.id !== id).sort((a, b) => a.y - b.y || a.x - b.x);
  const displaced: typeof others = [];
  for (const it of others) {
    const [w, h] = dims(it.size);
    if (occ.fits(it.x, it.y, w, h)) {
      occ.take(it.x, it.y, w, h);
      next.set(it.id, { x: it.x, y: it.y });
    } else displaced.push(it);
  }
  for (const it of displaced) {
    const [w, h] = dims(it.size);
    const [fx, fy] = occ.firstFree(w, h, it.y);
    occ.take(fx, fy, w, h);
    next.set(it.id, { x: fx, y: fy });
  }
  return items.map((i) => ({ ...i, ...next.get(i.id)! }));
}

export function rowsUsed(items: { size: string; y: number }[]): number {
  return items.reduce((m, i) => Math.max(m, i.y + dims(i.size)[1]), 0);
}
